import React, { useState, useEffect } from "react";
import Web3Service, {
  UserDetails,
  subtractDecimals,
  formatWithCommas,
  truncateDecimals,
  hasPositiveBalance,
  formatUnits,
} from "../services/Web3Services";
import SuccessPage from "./SuccessPage";
import { Check, Copy } from "lucide-react";
import logo from "../media/logo.png";
import cv_token2 from "../media/26566.png";

// Reusable components
export const CVSwapLogo = () => (
  <div className="flex items-center gap-2">
    <img src={logo} alt="CVSwap Logo" className="h-6 w-auto object-contain" />
  </div>
);

export const CVTokenLogo2 = () => (
  <div className="flex items-center gap-2">
    <img
      src={cv_token2}
      alt="CVToken Logo"
      className="h-6 w-auto object-contain"
    />
    <span className="text-xl font-bold text-white">CVT V2</span>
  </div>
);

// Prop interface
interface VestingDashboardProps {
  account: string;
  userState: UserDetails | null;
  onDepositFee: () => Promise<boolean>;
  onClaimTokens: () => Promise<void>;
  isActionInProgress: boolean;
  statusMessage: { type: "success" | "error"; text: string } | null;
}

// Data structure for the vesting schedule display
interface VestingScheduleData {
  totalVested: string;
  vestingStartDate: Date | null;
  totalReleased: string;
  pendingTokens: string;
  nextReleaseDate: Date | null;
  weeklyReleasePercent: number;
}

const VestingDashboard: React.FC<VestingDashboardProps> = ({
  account,
  userState,
  onDepositFee,
  onClaimTokens,
  isActionInProgress,
  statusMessage,
}) => {
  const [copied, setCopied] = useState(false);
  const [showSuccessPage, setShowSuccessPage] = useState(false);
  const [vestingSchedule, setVestingSchedule] =
    useState<VestingScheduleData | null>(null);
  const [isCopied, setIsCopied] = useState(false);

  // --- State Management and Data Fetching ---

  // Effect to calculate and format the vesting schedule for display
  useEffect(() => {
    const calculateVestingSchedule = async () => {
      if (!userState || !account) return;

      try {
        const params = await Web3Service.getVestingParams(account);
        const schedule: VestingScheduleData = {
          totalVested: userState.vestedToken,
          vestingStartDate:
            userState.startTime > 0
              ? new Date(userState.startTime * 1000)
              : null,
          totalReleased: userState.tokenReleased,
          pendingTokens: subtractDecimals(
            userState.vestedToken,
            userState.tokenReleased
          ),
          nextReleaseDate: null, // This will be calculated below if applicable
          weeklyReleasePercent: params.weeklyPercent / 100,
        };

        if (userState.startTime > 0 && userState.firstClaimDone) {
          const lastClaimDate = new Date(userState.startTime * 1000);
          const weeksSinceStart = Math.floor(
            (Date.now() - lastClaimDate.getTime()) / (7 * 24 * 60 * 60 * 1000)
          );
          const nextReleaseWeek = weeksSinceStart + 1;
          schedule.nextReleaseDate = new Date(
            lastClaimDate.getTime() + nextReleaseWeek * 7 * 24 * 60 * 60 * 1000
          );
        }
        setVestingSchedule(schedule);
      } catch (error) {
        console.error("Error calculating vesting schedule:", error);
      }
    };

    calculateVestingSchedule();
  }, [userState, account]);

  // --- Handlers ---

  const handlePayFee = async () => {
    await onDepositFee();
  };

  const handleClaim = async () => {
    await onClaimTokens();
    // Only show success page on the very first claim
    if (userState?.firstClaimDone) {
      setShowSuccessPage(true);
    }
  };

  // --- Helpers ---

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Exact: groups the whole part, truncates (never rounds) the fraction.
  const formatAmount = (amount: string | number) => {
    return formatWithCommas(amount.toString(), 4);
  };
  const CVT_V2_CONTRACT_ADDRESS = "0x59D5E8C6bfBD6575f3591d55877F41f6b647eaeE";

  const handleCopyToClipboard = () => {
    // Prevents re-clicking while the checkmark is visible
    if (isCopied) return;

    navigator.clipboard
      .writeText(CVT_V2_CONTRACT_ADDRESS)
      .then(() => {
        setIsCopied(true);
        // Revert icon back to copy symbol after 2 seconds
        setTimeout(() => {
          setIsCopied(false);
        }, 2000);
      })
      .catch((err) => {
        console.error("Failed to copy address: ", err);
      });
  };

  const formatFee = (wei: string) => {
    if (!wei || wei === "0") return "0.00";
    // BNB is always 18 decimals; exact conversion, truncated for display.
    return truncateDecimals(formatUnits(wei, 18), 5);
  };
  // --- Render Logic ---

  if (showSuccessPage) {
    return (
      <SuccessPage
        account={account}
        amountDeposited={userState?.vestedToken || "0"}
        claimableAmount={userState?.claimableAmount || "0"}
        onBack={() => setShowSuccessPage(false)}
      />
    );
  }

  if (!userState) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white">
        Loading user data...
      </div>
    );
  }

  const renderActionButton = () => {
    // SCENARIO A: User has deposited but not paid fees
    if (hasPositiveBalance(userState.vestedToken) && !userState.feesDeposited) {
      return (
        <>
          <div className="flex justify-between items-center mb-3">
            <span className="text-gray-400 text-sm">Estimated Gas Fee:</span>
            <span className="text-gray-400 text-sm font-semibold">
              {formatFee(userState.feesTobePaid)} BNB
            </span>
          </div>
          <button
            onClick={handlePayFee}
            disabled={isActionInProgress}
            className="w-full font-semibold py-3 px-6 rounded-xl shadow-md disabled:opacity-50 flex items-center justify-center text-black"
            style={{
              background:
                "linear-gradient(to right,rgba(59, 177, 78, 0.98),rgba(80, 177, 96, 0.98))",
            }}
          >
            {isActionInProgress ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black mr-2"></div>
                Processing...
              </>
            ) : (
              "Claim CVT V2"
            )}
          </button>
        </>
      );
    }

    // SCENARIO B: Fee paid, but first claim not done
    if (userState.feesDeposited && !userState.firstClaimDone) {
      // The contract itself will prevent a claim if the amount is 0.
      // This check is for the UI to disable the button proactively.
      const isClaimable = hasPositiveBalance(userState.claimableAmount);
      return (
        <button
          onClick={handleClaim}
          disabled={isActionInProgress || !isClaimable}
          className="w-full font-semibold py-3 px-6 rounded-xl shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-black"
          style={{
            background:
              "linear-gradient(to right,rgba(59, 177, 78, 0.98),rgba(80, 177, 96, 0.98))",
          }}
        >
          {isActionInProgress ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black mr-2"></div>
              Claiming...
            </>
          ) : (
            `Claim ${formatAmount(userState.claimableAmount)} CVT`
          )}
        </button>
      );
    }

    // SCENARIO C: All done, no button needed
    return null;
  };

  return (
    <div className="min-h-screen w-full flex items-stretch justify-center font-sora">
      <div className="w-full sm:max-w-md mx-auto">
        <div
          className="min-h-screen text-white font-sans flex flex-col p-4 relative overflow-hidden"
          style={{
            background: `radial-gradient(1200px 700px at top left, rgb(2, 36, 19), rgb(0, 2, 5) 30%)`,
          }}
        >
          {/* Header with Logo and Wallet */}
          <header className="w-full flex justify-between items-center mb-10">
            <CVSwapLogo />
            <div className="bg-[#2E2E2E] rounded px-4 py-2 shadow-md">
              <p className="text-gray-200 text-sm font-mono flex items-center gap-2">
                {account.substring(0, 6)}...
                {account.substring(account.length - 4)}
                <button onClick={() => copyToClipboard(account)}>
                  {copied ? (
                    <Check size={16} className="text-green-400" />
                  ) : (
                    <Copy
                      size={16}
                      className="text-gray-500 hover:text-white"
                    />
                  )}
                </button>
              </p>
            </div>
          </header>

          {/* Main Content Area */}
          <main className="flex flex-column justify-center">
            <div className="max-w-md mx-auto w-full">
              {/* Text Content */}
              <div className="mb-8 text-center">
                <h1 className="text-3xl font-extrabold text-white leading-tight mb-3">
                  Swap your <span className="text-[#8BE68B]">CV</span>
                  <br /> Tokens.
                </h1>
                <p className="text-gray-400 text-sm font-sora font-semibold">
                  This DApp lets CVT V1 holders swap their tokens for CVT V2.
                  Simply connect your wallet, deposit your CVT V1, and receive
                  CVT V2 directly through the app.
                </p>
              </div>

              {/* Card Section - only show if first_claim_done is false */}
              {!userState.firstClaimDone && (
                <div className="bg-[#1A1A1A] rounded-2xl p-3 shadow-lg ">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-gray-400 text-sm">Vested Token:</span>
                    <span className="text-gray-400 text-sm font-semibold">
                      {formatAmount(userState.vestedToken)} CVT
                    </span>
                  </div>

                  {/* Total Vested / Claimable */}
                  <div className="bg-[#2B2B2B] rounded-xl p-2 mb-4 flex justify-between items-center">
                    <div className="flex flex-col">
                      <CVTokenLogo2 />
                    </div>
                    <div className="text-right">
                      <div className="text-white text-xl font-bold">
                        {formatAmount(
                          userState.feesDeposited
                            ? userState.claimableAmount
                            : userState.vestedToken
                        )}
                        <div className="text-xs text-gray-400 font-normal">
                          {userState.feesDeposited
                            ? "Claimable Now"
                            : "Total Vested"}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Action Button */}
                  {renderActionButton()}
                </div>
              )}

              {/* Vested Schedule Grid */}
              <div className="mt-6 bg-[#1A1A1A] p-4 rounded-2xl border border-[#333]">
                <h2 className="text-red-400 text-md font-bold mb-3">
                  Vested Schedule
                </h2>
                <div className="grid grid-cols-2 gap-y-2 text-sm text-gray-300">
                  <div className="font-semibold">Total Vested:</div>
                  <div>
                    {formatAmount(vestingSchedule?.totalVested || "0")} CVT
                  </div>
                  <div className="font-semibold">Vesting Start:</div>
                  <div>
                    {vestingSchedule?.vestingStartDate
                      ? vestingSchedule.vestingStartDate.toLocaleString()
                      : "Not Started"}
                  </div>
                  <div className="font-semibold">Total Released:</div>
                  <div>
                    {formatAmount(vestingSchedule?.totalReleased || "0")} CVT
                  </div>
                  <div className="font-semibold">Pending Tokens:</div>
                  <div>
                    {formatAmount(vestingSchedule?.pendingTokens || "0")} CVT
                  </div>
                  <div className="font-semibold">Next Release:</div>
                  <div>
                    {vestingSchedule?.nextReleaseDate
                      ? vestingSchedule.nextReleaseDate.toLocaleDateString()
                      : "—"}
                  </div>
                  <div className="font-semibold">Weekly Release:</div>
                  <div>{vestingSchedule?.weeklyReleasePercent || "0"}%</div>
                </div>
              </div>

              {/* --- Updated Copyable Address Box --- */}
              {userState.firstClaimDone && (
                <>
                  <div className="mt-4">
                    <div
                      onClick={handleCopyToClipboard}
                      className="flex items-center justify-between w-full bg-[#1A1A1A] border border-[#333] rounded-xl p-3 pr-2 cursor-pointer group"
                    >
                      <span className="text-gray-400 font-mono text-xs truncate pl-1">
                        {CVT_V2_CONTRACT_ADDRESS}
                      </span>
                      <div
                        className="p-2 rounded-lg text-gray-400 transition-all duration-200 ease-in-out group-hover:bg-[#2c2c2c]"
                        aria-label="Copy contract address"
                      >
                        {isCopied ? (
                          // Checkmark Icon
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 text-green-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        ) : (
                          // Copy Icon
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                            />
                          </svg>
                        )}
                      </div>
                    </div>

                    <p className="text-center text-sm text-gray-200 mt-2">
                      Click to copy new CVT V2 address.
                    </p>
                  </div>
                </>
              )}

              {/* Status Message */}
              {statusMessage && statusMessage.type === "success" && (
                <div
                  className={`mt-4 text-center text-sm ${
                    statusMessage.type === "success"
                      ? "text-green-400"
                      : "text-red-400"
                  }`}
                >
                  {statusMessage.text}
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default VestingDashboard;
